import { useMemo } from "react";
import { useSelector } from "react-redux";
import { ROLES, getRoleLabel, normalizeRole, roleCanAccess } from "../config/roles";
import { getPermissionsForRole } from "../config/permissions";

/**
 * Central place to read the signed-in ops user's role and check access.
 * Prefer `usePermissions()` for UI gates; this hook remains for role labels and legacy `can()`.
 */
export default function useRole() {
  const user = useSelector((state) => state.auth.user);
  const role = user?.role;
  const roleKey = normalizeRole(role);
  const permissions = user?.permissions ?? getPermissionsForRole(roleKey || role);

  return useMemo(
    () => ({
      user,
      role: roleKey || role,
      roleLabel: getRoleLabel(roleKey || role),
      isSuperAdmin: roleKey === ROLES.SUPER_ADMIN,
      isExecutiveManager: roleKey === ROLES.EXECUTIVE_MANAGER,
      isCallCenterManager: roleKey === ROLES.CALL_CENTER_MANAGER,
      isCallCenterAgent: roleKey === ROLES.CALL_CENTER_AGENT,
      isLegalOfficer: roleKey === ROLES.LEGAL_OFFICER,
      isReadOnly:
        roleKey === ROLES.EXECUTIVE_MANAGER &&
        !permissions.some((p) => p.endsWith(".update") || p.endsWith(".manage") || p.endsWith(".assign")),
      can: (allowedRoles) => roleCanAccess(roleKey || role, allowedRoles),
    }),
    [permissions, role, roleKey, user]
  );
}
